// Permanent-delete state machine shared by Record Labels, Artists, and Albums.
//
// Lifted from the Song Library (src/pages/songs.tsx: openPermanentDelete,
// openBulkPermanentDelete, handleConfirmPermanentDelete, closeDeleteModal) so
// the three catalog surfaces cannot drift from the flow the operator already
// knows. Behavioural rules preserved verbatim:
//
//   - Opening always resets preflight/token/reason/phase first, so no stale
//     confirmation can leak into the next operation.
//   - Bulk permanent delete skips the generic batch pre-confirm and comes
//     straight here — one confirmation experience only.
//   - Every exit path (success, partial failure, total failure, cancel) fully
//     resets, so the next batch starts without a browser refresh.

import { useCallback, useState } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import {
  catalogBulkPurgeFailuresFromResponse,
  DELETE_CONFIRM_TOKEN,
  type CatalogBulkPurgeResponse,
  type CatalogEntityKind,
  type CatalogPurgeCount,
  type CatalogPurgeFailure,
  type CatalogPurgePreflight,
} from './catalogLifecycleTypes';
import { catalogCopy } from './catalogEntityCopy';
import type { CatalogPurgePhase } from './CatalogPermanentDeleteModal';

/** Matches the Song Library's bounded bulk preflight aggregation. */
const MAX_BULK_PREFLIGHT = 25;

export interface CatalogPurgeTarget {
  id: string;
  name: string;
}

export interface UseCatalogPurgeFlowArgs {
  kind: CatalogEntityKind;
  canLifecycle: boolean;
  message: MessageInstance;
  preflight: (id: string) => Promise<CatalogPurgePreflight>;
  purge: (args: { id: string; name: string; reason: string }) => Promise<unknown>;
  bulkPurge: (args: {
    ids: string[];
    reason: string;
  }) => Promise<CatalogBulkPurgeResponse>;
  extractErrorMessage: (err: unknown) => string | undefined;
  /** Called after any successful delete so the page can reconcile its lists. */
  onPurged?: () => void;
  /** Clears row selection + batch dropdown (see useBatchActions). */
  resetBatchUi: () => void;
}

/** Sum labelled count lines across a bulk selection, preserving label order. */
function mergeCounts(pages: CatalogPurgePreflight[]): CatalogPurgeCount[] {
  const totals = new Map<string, number>();
  for (const page of pages) {
    for (const count of page.counts || []) {
      totals.set(count.label, (totals.get(count.label) || 0) + (count.value || 0));
    }
  }
  return Array.from(totals, ([label, value]) => ({ label, value }));
}

export function useCatalogPurgeFlow({
  kind,
  canLifecycle,
  message,
  preflight,
  purge,
  bulkPurge,
  extractErrorMessage,
  onPurged,
  resetBatchUi,
}: UseCatalogPurgeFlowArgs) {
  const { noun, nounPlural, Noun } = catalogCopy(kind);

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<CatalogPurgeTarget | null>(null);
  const [bulkIds, setBulkIds] = useState<string[]>([]);
  const [preflightData, setPreflightData] = useState<CatalogPurgePreflight | null>(
    null,
  );
  const [reason, setReason] = useState('');
  const [confirmTyped, setConfirmTyped] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<CatalogPurgePhase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [failures, setFailures] = useState<CatalogPurgeFailure[]>([]);
  const [failuresOpen, setFailuresOpen] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setTarget(null);
    setBulkIds([]);
    setPreflightData(null);
    setReason('');
    setConfirmTyped('');
    setErrorMessage(null);
    setPhase('idle');
  }, []);

  const closeFailures = useCallback(() => {
    setFailuresOpen(false);
    setFailures([]);
  }, []);

  /** Cancel from the modal: reset the dialog AND the batch UI behind it. */
  const cancel = useCallback(() => {
    if (loading) return;
    close();
    resetBatchUi();
  }, [close, loading, resetBatchUi]);

  const beginOpen = useCallback(() => {
    setPreflightData(null);
    setReason('');
    setConfirmTyped('');
    setErrorMessage(null);
    setPhase('preflight');
    setOpen(true);
  }, []);

  const openSingle = useCallback(
    async (id: string, name: string) => {
      if (!canLifecycle) return;
      setTarget({ id, name });
      setBulkIds([]);
      beginOpen();
      try {
        const summary = await preflight(id);
        setPreflightData(summary);
        setPhase('confirm');
      } catch (err) {
        setErrorMessage(
          extractErrorMessage(err) || 'Could not load deletion preflight',
        );
        setPhase('error');
      }
    },
    [beginOpen, canLifecycle, extractErrorMessage, preflight],
  );

  const openBulk = useCallback(
    async (ids: string[]) => {
      if (!canLifecycle || ids.length === 0) return;
      setBulkIds(ids);
      setTarget({ id: ids[0], name: `${ids.length} selected ${nounPlural}` });
      beginOpen();
      try {
        const pages: CatalogPurgePreflight[] = [];
        for (const id of ids.slice(0, MAX_BULK_PREFLIGHT)) {
          // eslint-disable-next-line no-await-in-loop
          pages.push(await preflight(id));
        }
        const blockers = pages.flatMap((page) => page.blockers || []);
        setPreflightData({
          kind,
          id: ids[0],
          name: `${ids.length} selected ${nounPlural}`,
          status: 'archived',
          dryRun: true,
          eligible: blockers.length === 0,
          blockers,
          counts: mergeCounts(pages),
          referencingSongs: pages.flatMap((page) => page.referencingSongs || []),
          songLibraryPath: pages.find((page) => page.songLibraryPath)?.songLibraryPath ?? null,
          willRemove: [
            `Each selected ${Noun} record and its catalog identity`,
            'Catalog projection rows (list, search, and filter visibility)',
          ],
          willRetain: [
            `${Noun} purge tombstones in append-only audit history`,
            'Actor, reason, and dependency check recorded at purge time',
          ],
        });
        setPhase('confirm');
      } catch (err) {
        setErrorMessage(
          extractErrorMessage(err) || 'Could not load bulk deletion preflight',
        );
        setPhase('error');
      }
    },
    [beginOpen, canLifecycle, extractErrorMessage, kind, Noun, nounPlural, preflight],
  );

  const confirm = useCallback(async () => {
    if (!canLifecycle || !target) return;
    if (confirmTyped !== DELETE_CONFIRM_TOKEN) {
      message.error(`Type ${DELETE_CONFIRM_TOKEN} to confirm`);
      return;
    }
    if (!reason.trim()) {
      message.error('Deletion reason is required');
      return;
    }
    setLoading(true);
    setPhase('running');
    setErrorMessage(null);
    try {
      if (bulkIds.length > 0) {
        const res = await bulkPurge({ ids: bulkIds, reason: reason.trim() });
        const rows = catalogBulkPurgeFailuresFromResponse(res);
        if (res.succeeded > 0) onPurged?.();
        close();
        resetBatchUi();

        if (res.succeeded === 0 && res.failed > 0) {
          setFailures(rows);
          message.error(
            `Permanent delete failed for ${res.failed} ${noun}(s). View Failures for details.`,
            8,
          );
          setFailuresOpen(true);
        } else if (res.failed > 0) {
          setFailures(rows);
          message.warning(
            `Permanently deleted ${res.succeeded} ${noun}(s); ${res.failed} failed. View Failures for details.`,
            8,
          );
          setFailuresOpen(true);
        } else {
          setFailures([]);
          message.success(
            `Permanently deleted ${res.succeeded} ${noun}(s)` +
              (res.skipped ? `; ${res.skipped} skipped` : ''),
          );
        }
      } else {
        await purge({
          id: target.id,
          name: preflightData?.name || target.name,
          reason: reason.trim(),
        });
        onPurged?.();
        message.success(
          `${Noun} permanently deleted — record removed; tombstone retained in audit history`,
        );
        close();
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || 'Permanent delete failed';
      setErrorMessage(msg);
      setPhase('error');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [
    bulkIds,
    bulkPurge,
    canLifecycle,
    close,
    confirmTyped,
    extractErrorMessage,
    message,
    noun,
    Noun,
    onPurged,
    preflightData,
    purge,
    reason,
    resetBatchUi,
    target,
  ]);

  return {
    modalProps: {
      kind,
      open,
      phase,
      preflight: preflightData,
      errorMessage,
      targetName: target?.name,
      targetId: target?.id,
      bulkCount: bulkIds.length,
      confirmTyped,
      onConfirmTypedChange: setConfirmTyped,
      reason,
      onReasonChange: setReason,
      loading,
      onCancel: cancel,
      onConfirm: () => void confirm(),
    },
    failuresProps: {
      kind,
      open: failuresOpen,
      failures,
      onClose: closeFailures,
    },
    openSingle,
    openBulk,
    close,
  };
}
